<template>
  <v-dialog
      v-model="dialog"
      :width="dialogWidth"
      @keydown.enter.prevent="onenter"
  >
    <v-card
        class="resizable-card"
        :class="{ 'no-select': resizing }"
        :style="{ height: dialogHeight + 'px' }"
    >
      <v-card-title>
        <span class="headline">Boat Order ({{ rules.name }})</span>
        <v-spacer/>
        <r-authorize
            :master="masterAccess"
            :locked.sync="model.locked"
            :enabled.sync="model.enabled"
        />
      </v-card-title>

      <v-card-text class="resizable-card__body">
        <v-col cols="4" sm="2" md="2">
          <v-btn @click="showPreview()" class="mr-2" small>View JSON</v-btn>
        </v-col>
        <v-row dense class="mt-3">
          <v-col cols="12" md="12" sm="12">
            <v-text-field
                disabled
                label="UID:"
                v-model.trim="model.uid"
                hide-details
                outlined
                dense
            />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="6" sm="6">
            <v-text-field
                label="Name:"
                v-model.trim="model.name"
                v-ascii-only
            />
          </v-col>
          <v-col cols="12" md="6" sm="6">
            <v-text-field
                label="Key:"
                v-model.trim="model.key"
                v-ascii-only
            />
          </v-col>
          <v-col cols="12" md="3" sm="3">
            <r-boat-kind :model.sync="model.boat" label="Boat:"/>
          </v-col>
          <v-col cols="12" md="3" sm="3">
            <v-text-field label="Order:" v-model.number="model.order"/>
          </v-col>
          <v-col cols="12" sm="3" md="3">
            <v-checkbox v-model="model.priceGuard" label="Price Guard" hide-details/>
          </v-col>
          <v-col cols="12" md="6" sm="6">
            <r-ad-placement-dropdown :model.sync="model.adPlacement" title="Ad Placement" hide-details/>
          </v-col>
        </v-row>
        <v-expansion-panels>
          <r-guard-panel :model.sync="model.guard"/>
          <r-currency-panel :items.sync="model.price" title="Price"/>
          <r-reward-panel :model.sync="model.reward" title="Rewards"/>
        </v-expansion-panels>
      </v-card-text>

      <v-card-actions class="resizable-card__actions">
        <v-spacer/>
        <v-btn @click="cancel" color="blue darken-1" text>
          Cancel
        </v-btn>
        <v-btn
            @click="save"
            color="blue darken-1"
            text
            v-if="!lockedAssess || masterAccess"
        >
          Save
        </v-btn>
      </v-card-actions>

      <div
          class="resize-handle"
          @mousedown.stop.prevent="startResize"
      ></div>
      <r-preview-dialog ref="preview-dialog"></r-preview-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import VueTypes from "vue-types";
import { ViewRules } from "@/common/ViewRules";
import { BoatOrder } from "@/modules/redactor/models/boats/BoatOrder";
import { RedactorUtils } from "@/modules/redactor/core/RedactorUtils";

import RGuardPanel from "@/modules/redactor/views/guard/r-guard-panel";
import RBoatKind from "@/modules/redactor/views/boats/r-boat-kind";
import RRewardPanel from "@/modules/redactor/views/reward/r-reward-panel.vue";
import RAuthorize from "@/modules/redactor/views/common/r-authorize";
import RCurrencyPanel from "@/modules/redactor/views/currency/r-currency-panel";
import RPreviewDialog from "@/modules/redactor/views/preview/r-preview-dialog.vue";
import RAdPlacementDropdown from "@/modules/redactor/views/ad-settings/placement/r-ad-placement-dropdown.vue";

export default {
  name: "r-boat-order-dialog",
  components: {RAdPlacementDropdown, RPreviewDialog, RCurrencyPanel, RAuthorize, RRewardPanel, RBoatKind, RGuardPanel },
  data: () => ({
    dialog: false,
    resolve: null,
    reject: null,
    model: VueTypes.objectOf(BoatOrder),
    rules: ViewRules.READONLY,

    dialogWidth: 805,
    dialogHeight: 680,

    resizing: false,
    resizeStartX: 0,
    resizeStartY: 0,
    resizeStartWidth: 805,
    resizeStartHeight: 680,
    viewportPadding: 32
  }),
  computed: {
    lockedAssess() {
      return this.model.locked;
    },
    masterAccess() {
      return RedactorUtils.hasMasterAccess();
    }
  },
  methods: {
    show(rules, model) {
      this.dialog = true;
      this.rules = rules;
      this.model = model || new BoatOrder();

      if (ViewRules.COPY.equal(this.rules)) {
        this.model.locked = false;
      }

      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },
    save() {
      if (this.model.valid()) {
        this.resolve && this.resolve(this.model);
        this.discard();
      } else {
        this.$alert("Invalid config!");
      }
    },
    cancel() {
      this.reject && this.reject();
      this.discard();
    },
    discard() {
      this.dialog = false;
      this.stopResize();
    },
    onenter() {
      if (!this.lockedAssess || this.masterAccess) {
        this.save();
      } else {
        this.cancel();
      }
    },
    startResize(e) {
      this.resizing = true;
      this.resizeStartX = e.clientX;
      this.resizeStartY = e.clientY;
      this.resizeStartWidth = this.dialogWidth;
      this.resizeStartHeight = this.dialogHeight;
      window.addEventListener("mousemove", this.onResize);
      window.addEventListener("mouseup", this.stopResize);
    },
    onResize(e) {
      if (!this.resizing) return;
      const dx = e.clientX - this.resizeStartX;
      const dy = e.clientY - this.resizeStartY;

      let newWidth = this.resizeStartWidth + dx;
      let newHeight = this.resizeStartHeight + dy;

      const minWidth = 550;
      const minHeight = 360;

      if (newWidth < minWidth) newWidth = minWidth;
      if (newHeight < minHeight) newHeight = minHeight;

      this.dialogWidth = newWidth;
      this.dialogHeight = newHeight;
    },
    stopResize() {
      if (!this.resizing) return;
      this.resizing = false;
      window.removeEventListener("mousemove", this.onResize);
      window.removeEventListener("mouseup", this.stopResize);
    },
    showPreview() {
      this.$refs['preview-dialog'].show(this.rules, this.model, BoatOrder)
          .then((result) => {
            Object.assign(this.model, result);
          })
          .catch((e) => {
            this.$alert("Failed to parse config!" + e.message);
          });
    }
  },
  beforeDestroy() {
    window.removeEventListener("mousemove", this.onResize);
    window.removeEventListener("mouseup", this.stopResize);
  }
};
</script>

<style lang="css" scoped>
:read-only {
  white-space: nowrap;
}
.resizable-card {
  position: relative;
  display: flex;
  flex-direction: column;
}
.resizable-card__body {
  flex: 1;
  overflow: auto;
}
.resizable-card__actions {
  flex-shrink: 0;
}
.resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  cursor: se-resize;
  border-right: 2px solid rgba(0, 0, 0, 0.3);
  border-bottom: 2px solid rgba(0, 0, 0, 0.3);
}
.no-select {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}
</style>